/**
 * Who may see the onboarding wizard (BH-08).
 *
 * Submitting the wizard regenerates the assessment path, and regeneration
 * deactivates the active one. /onboarding used to render the wizard for anyone,
 * so a user who had finished onboarding and merely revisited the URL could
 * replace the path they were partway through.
 *
 *   still loading the profile               → 'loading'
 *   onboarding done, no ?retake             → 'redirect'  (to the dashboard)
 *   onboarding done, ?retake, not confirmed → 'confirm-retake'
 *   onboarding done, ?retake, confirmed     → 'wizard'    (submits retake: true)
 *   not onboarded, or the profile unreadable→ 'wizard'
 *
 * An unreadable profile shows the wizard rather than trapping a new user: the
 * server keeps an existing active path unless `retake: true` is sent, so a
 * submit from that state cannot wipe anything.
 */

export type OnboardingEntry = 'loading' | 'redirect' | 'confirm-retake' | 'wizard';

export interface OnboardingEntryInput {
  isLoading: boolean;
  hasCompletedOnboarding: boolean;
  hasProfile: boolean;
  retakeRequested: boolean;
  retakeConfirmed: boolean;
}

export function onboardingEntryDecision(input: OnboardingEntryInput): OnboardingEntry {
  if (input.isLoading) return 'loading';
  const completed = input.hasCompletedOnboarding && input.hasProfile;
  if (!completed) return 'wizard';
  if (!input.retakeRequested) return 'redirect';
  return input.retakeConfirmed ? 'wizard' : 'confirm-retake';
}

/** `?retake=1` / `?retake=true` — the only way into the wizard after onboarding. */
export function isRetakeRequested(search: string): boolean {
  const v = new URLSearchParams(search).get('retake');
  return v === '1' || v === 'true';
}
